<template>
  <b-modal
    ref="erpModal"
    id="erp-download-modal"
    class="erp-modal"
    title="Download ERP Data"
    size="md"
    hide-footer
    @hidden="resetForm"
  >
    <template #modal-header="{ close }">
      <div class="compact-header">
        <div class="header-icon">
          <i class="las la-download"></i>
        </div>
        <h5 class="header-title">Download ERP Data</h5>
        <button type="button" class="close-btn" @click="close()" aria-label="Close">
          <i class="las la-times"></i>
        </button>
      </div>
    </template>

    <div class="compact-modal-body">
      <!-- Business Selection -->
      <div class="section">
        <div class="section-header">
          <i class="las la-building section-icon"></i>
          <span class="section-title">Selected Business</span>
        </div>
        <div class="business-card">
          <div class="business-name">{{ (selectedBusiness && selectedBusiness.name) || 'No business selected' }}</div>
          <div class="business-email">{{ (selectedBusiness && selectedBusiness.admin_user && selectedBusiness.admin_user.email) || '-' }}</div>
        </div>
      </div>

      <!-- ERP Platform Selection -->
      <div class="section">
        <div class="section-header">
          <i class="las la-cogs section-icon"></i>
          <span class="section-title">Select ERP Platform</span>
        </div>
        <div v-if="checkingIntegration" class="loading-message">
          <i class="spinner-border spinner-border-sm"></i>
          <p>Checking for active integrations...</p>
        </div>
        <div v-else-if="!checkingIntegration && !integrationData" class="no-integration-message">
          <i class="las la-exclamation-triangle"></i>
          <p>No ERP integrations are currently active for this business. Please configure an integration first.</p>
        </div>
        <div v-else-if="!checkingIntegration && integrationData" class="platform-grid">
          <div
            v-for="platform in availablePlatforms"
            :key="platform.value"
            class="platform-item"
            :class="{ 
              active: selectedPlatform === platform.value,
              disabled: platform.isDisabled
            }"
            @click="selectPlatform(platform.value, platform.isDisabled)"
          >
            <i :class="platform.icon" class="platform-icon"></i>
            <span class="platform-name">{{ platform.name }}</span>
            <i v-if="platform.isActive" class="las la-check-circle active-indicator"></i>
          </div>
        </div>
      </div>

      <!-- Date Range -->
      <div class="section">
        <div class="section-header">
          <i class="las la-calendar section-icon"></i>
          <span class="section-title">Date Range (Optional)</span>
        </div>
        <div class="date-preset-chips">
          <div
            v-for="preset in datePresetOptions"
            :key="preset.value"
            class="preset-chip"
            :class="{ active: dateRangePreset === preset.value }"
            @click="selectDatePreset(preset.value)"
          >
            {{ preset.label }}
          </div>
        </div>
        <div v-if="dateRangePreset === 'custom'" class="date-range-fields">
          <div class="date-field">
            <label for="start-date">Start Date</label>
            <input
              id="start-date"
              type="date"
              class="date-input"
              v-model="startDate"
            />
          </div>
          <div class="date-field">
            <label for="end-date">End Date</label>
            <input
              id="end-date"
              type="date"
              class="date-input"
              v-model="endDate"
            />
          </div>
        </div>
      </div>

      <!-- Options -->
      <div class="section">
        <div class="section-header">
          <i class="las la-sliders-h section-icon"></i>
          <span class="section-title">Options</span>
        </div>
        <div class="option-row">
          <label class="option-label" for="calculate-payer-score">Calculate payer score</label>
          <b-form-checkbox
            id="calculate-payer-score"
            v-model="calculatePayerScore"
            switch
            class="option-switch"
          />
        </div>
      </div>

      <!-- Email Configuration -->
      <div class="section">
        <!-- To Emails -->
        <div class="email-group">
          <div class="section-header">
            <i class="las la-envelope section-icon"></i>
            <span class="section-title">To Emails <span class="required">*</span></span>
          </div>
          <input
            type="email"
            class="email-input"
            placeholder="Enter email address and press Enter"
            v-model="newToEmail"
            @keydown.enter.prevent="addToEmail"
            @blur="addToEmail"
          />
          <div class="email-tags" v-if="toEmails.length > 0">
            <span v-for="(email, index) in toEmails" :key="`to-${index}`" class="email-tag">
              {{ email }}
              <i class="las la-times tag-remove" @click="removeToEmail(index)"></i>
            </span>
          </div>
        </div>

        <!-- CC Emails -->
        <div class="email-group">
          <div class="section-header">
            <i class="las la-copy section-icon"></i>
            <span class="section-title">CC Emails (Optional)</span>
          </div>
          <input
            type="email"
            class="email-input"
            placeholder="Enter CC email address and press Enter"
            v-model="newCcEmail"
            @keydown.enter.prevent="addCcEmail"
            @blur="addCcEmail"
          />
          <div class="email-tags" v-if="ccEmails.length > 0">
            <span v-for="(email, index) in ccEmails" :key="`cc-${index}`" class="email-tag">
              {{ email }}
              <i class="las la-times tag-remove" @click="removeCcEmail(index)"></i>
            </span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <button class="btn-cancel" @click="closeModal" :disabled="loading">
          Cancel
        </button>
        <button class="btn-download" @click="downloadERPData" :disabled="!canDownload || loading">
          <i v-if="loading" class="spinner-border spinner-border-sm"></i>
          <i v-else class="las la-download"></i>
          {{ loading ? 'Downloading...' : 'Download ERP Data' }}
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import ERPService from '@/services/erp.service';

export default {
  name: 'ERPModal',
  data() {
    return {
      selectedBusiness: null,
      selectedPlatform: '',
      dateRangePreset: '',
      startDate: '',
      endDate: '',
      toEmails: [],
      datePresetOptions: [
        { value: '', label: 'None' },
        { value: '1', label: 'Last 1 year' },
        { value: '2', label: 'Last 2 years' },
        { value: '3', label: 'Last 3 years' },
        { value: '5', label: 'Last 5 years' },
        { value: 'custom', label: 'Custom' }
      ],
      ccEmails: [],
      newToEmail: '',
      newCcEmail: '',
      calculatePayerScore: false,
      loading: false,
      checkingIntegration: false,
      integrationData: null,
      erpPlatforms: [
        {
          name: 'QuickBooks',
          value: 'QUICKBOOKS',
          icon: 'las la-calculator',
          color: '#0077C5'
        },
        {
          name: 'Xero',
          value: 'XERO',
          icon: 'las la-chart-line',
          color: '#13B5EA'
        },
        {
          name: 'Zoho',
          value: 'ZOHO',
          icon: 'las la-briefcase',
          color: '#C8102E'
        },
        {
          name: 'Dynamics',
          value: 'DYNAMICS',
          icon: 'las la-cube',
          color: '#0078D4'
        },
        {
          name: 'Odoo',
          value: 'ODOO',
          icon: 'las la-cog',
          color: '#714B67'
        },
        {
          name: 'Wafeq',
          value: 'WAFEQ',
          icon: 'las la-file-invoice-dollar',
          color: '#FF6B35'
        }
      ]
    };
  },
  computed: {
    canDownload() {
      return (
        this.selectedBusiness &&
        this.selectedPlatform &&
        this.toEmails.length > 0 &&
        this.activePlatform // Only allow download if there's an active integration
      );
    },
    availablePlatforms() {
      if (!this.selectedBusiness) return this.erpPlatforms;
      
      return this.erpPlatforms.map(platform => {
        let isActive = false;
        let isDisabled = true;
        
        // Check which integration is active based on API response
        if (this.integrationData && this.integrationData.type) {
          // Direct comparison with API response type values
          const apiType = this.integrationData.type;
          const platformValue = platform.value;
          
          // Check if this platform matches the active integration
          isActive = apiType === platformValue;
        }
        
        return {
          ...platform,
          isActive,
          isDisabled: !isActive
        };
      });
    },
    activePlatform() {
      return this.availablePlatforms.find(platform => platform.isActive);
    }
  },
  methods: {
    async show(business = null) {
      console.log('ERP Modal show method called with business:', business);
      this.selectedBusiness = business;
      
      // Check for active integration when modal opens
      if (business && business.id) {
        await this.checkActiveIntegration(business.id);
      }
      
      // Auto-select the active platform if available
      this.$nextTick(() => {
        if (this.activePlatform) {
          this.selectedPlatform = this.activePlatform.value;
        }
      });
      
      // Ensure the modal reference is available
      this.$nextTick(() => {
        if (this.$refs.erpModal && typeof this.$refs.erpModal.show === 'function') {
          this.$refs.erpModal.show();
        } else {
          console.error('Bootstrap modal reference not found:', this.$refs.erpModal);
        }
      });
    },
    closeModal() {
      if (this.$refs.erpModal && typeof this.$refs.erpModal.hide === 'function') {
        this.$refs.erpModal.hide();
      } else {
        console.error('Bootstrap modal reference not found for closing:', this.$refs.erpModal);
      }
    },
    resetForm() {
      this.selectedBusiness = null;
      this.selectedPlatform = '';
      this.dateRangePreset = '';
      this.startDate = '';
      this.endDate = '';
      this.toEmails = [];
      this.ccEmails = [];
      this.newToEmail = '';
      this.newCcEmail = '';
      this.calculatePayerScore = false;
      this.loading = false;
      this.checkingIntegration = false;
      this.integrationData = null;
    },
    selectPlatform(platform, isDisabled = false) {
      // Prevent selection of disabled platforms
      if (isDisabled) {
        return;
      }
      this.selectedPlatform = platform;
    },
    selectDatePreset(value) {
      this.dateRangePreset = value;
      if (value === '' || value === 'custom') {
        if (value === '') {
          this.startDate = '';
          this.endDate = '';
        }
        return;
      }
      this.applyDateRange(parseInt(value, 10));
    },
    applyDateRange(years) {
      const end = new Date();
      const start = new Date();
      start.setFullYear(start.getFullYear() - years);
      this.endDate = end.toISOString().split('T')[0];
      this.startDate = start.toISOString().split('T')[0];
    },
    addToEmail() {
      if (this.newToEmail && this.isValidEmail(this.newToEmail)) {
        if (!this.toEmails.includes(this.newToEmail)) {
          this.toEmails.push(this.newToEmail);
        }
        this.newToEmail = '';
      }
    },
    removeToEmail(index) {
      this.toEmails.splice(index, 1);
    },
    addCcEmail() {
      if (this.newCcEmail && this.isValidEmail(this.newCcEmail)) {
        if (!this.ccEmails.includes(this.newCcEmail)) {
          this.ccEmails.push(this.newCcEmail);
        }
        this.newCcEmail = '';
      }
    },
    removeCcEmail(index) {
      this.ccEmails.splice(index, 1);
    },
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    getPlatformIconStyle(platform) {
      return {
        color: this.selectedPlatform === platform.value ? platform.color : '#6c757d'
      };
    },
    async downloadERPData() {
      if (!this.canDownload) return;

      this.loading = true;

      try {
        const payload = {
          business_id: this.selectedBusiness.id,
          platform: this.selectedPlatform,
          business_name: this.selectedBusiness.name,
          to_emails: this.toEmails,
          cc_emails: this.ccEmails,
          calculate_payer_score: Boolean(this.calculatePayerScore),
          filters: {
            start_date: this.startDate || '',
            end_date: this.endDate || ''
          }
        };

        await ERPService.downloadERPData(payload);
        
        this.$showAlert(
          `ERP data download request has been submitted successfully! You will receive the data via email shortly.`,
          'success'
        );
        
        this.closeModal();
      } catch (error) {
        console.error('ERP download error:', error);
        this.$showAlert(
          error.message || 'Failed to submit ERP data download request. Please try again.',
          'danger'
        );
      } finally {
        this.loading = false;
      }
    },
    async checkActiveIntegration(businessId) {
      this.checkingIntegration = true;
      this.integrationData = null;
      
      try {
        const response = await ERPService.checkIntegration(businessId);
        this.integrationData = response?.data;
      } catch (error) {
        // If error (no integration), integrationData remains null
        this.integrationData = null;
      } finally {
        this.checkingIntegration = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.erp-modal {
  .modal-dialog {
    max-width: 500px;
  }
}

.compact-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  margin: -1rem -1.5rem 0;
  border-radius: 0.375rem 0.375rem 0 0;

  .header-icon {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;

    i {
      font-size: 1.2rem;
    }
  }

  .header-title {
    flex: 1;
    margin: 0;
    font-weight: 600;
    font-size: 1.1rem;
  }

  .close-btn {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: scale(1.05);
    }

    i {
      font-size: 1rem;
    }
  }
}

.compact-modal-body {
  padding: 1.5rem;
  max-height: 70vh;
  overflow-y: auto;
}

.section {
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;

  .section-icon {
    color: #667eea;
    font-size: 1.1rem;
    margin-right: 0.5rem;
  }

  .section-title {
    font-weight: 600;
    color: #2c3e50;
    font-size: 0.9rem;
  }

  .required {
    color: #e74c3c;
    margin-left: 2px;
  }
}

.business-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.75rem 1rem;

  .business-name {
    font-weight: 600;
    color: #2c3e50;
    font-size: 0.95rem;
    margin-bottom: 0.25rem;
  }

  .business-email {
    color: #6c757d;
    font-size: 0.8rem;
  }
}

.loading-message {
  text-align: center;
  padding: 2rem 1rem;
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 8px;
  color: #1565c0;

  i {
    margin-bottom: 0.5rem;
    display: block;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 500;
  }
}

.no-integration-message {
  text-align: center;
  padding: 2rem 1rem;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  color: #856404;

  i {
    font-size: 2rem;
    margin-bottom: 0.5rem;
    display: block;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 500;
  }
}

.platform-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 0.75rem;
}

.platform-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem 0.5rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  position: relative;

  &:hover:not(.disabled) {
    border-color: #667eea;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
  }

  &.active {
    border-color: #667eea;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);

    .platform-icon {
      color: white;
    }
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #f8f9fa;
    border-color: #dee2e6;

    &:hover {
      transform: none;
      box-shadow: none;
      border-color: #dee2e6;
    }

    .platform-icon {
      color: #adb5bd;
    }

    .platform-name {
      color: #adb5bd;
    }
  }

  .platform-icon {
    font-size: 1.5rem;
    margin-bottom: 0.25rem;
    color: #6c757d;
    transition: color 0.2s ease;
  }

  .platform-name {
    font-size: 0.75rem;
    font-weight: 500;
    text-align: center;
  }

  .active-indicator {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    color: #28a745;
    font-size: 1rem;
    background: white;
    border-radius: 50%;
    padding: 0.1rem;
  }
}

.date-preset-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.preset-chip {
  padding: 0.35rem 0.65rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;

  &:hover {
    border-color: #667eea;
    color: #667eea;
  }

  &.active {
    border-color: #667eea;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }
}

.date-range-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.date-field {
  label {
    display: block;
    font-size: 0.85rem;
    font-weight: 500;
    color: #6c757d;
    margin-bottom: 0.4rem;
  }
}

.date-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
}

.option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.65rem 0.85rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.option-label {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: #2c3e50;
  cursor: pointer;
}

.option-switch {
  flex-shrink: 0;
  margin: 0;
}

.email-group {
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.email-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }

  &::placeholder {
    color: #adb5bd;
    font-size: 0.85rem;
  }
}

.email-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
}

.email-tag {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;

  .tag-remove {
    margin-left: 0.4rem;
    cursor: pointer;
    opacity: 0.8;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }
  }
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
}

.btn-cancel {
  padding: 0.5rem 1.25rem;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  color: #6c757d;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #e9ecef;
    border-color: #adb5bd;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-download {
  padding: 0.5rem 1.5rem;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  border: none;
  border-radius: 6px;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  i {
    font-size: 1rem;
  }
}

// Responsive adjustments
@media (max-width: 576px) {
  .compact-modal-body {
    padding: 1rem;
  }

  .date-range-fields {
    grid-template-columns: 1fr;
  }

  .platform-grid {
    grid-template-columns: repeat(auto-fit, minmax(70px, 1fr));
    gap: 0.5rem;
  }

  .platform-item {
    padding: 0.5rem 0.25rem;

    .platform-icon {
      font-size: 1.25rem;
    }

    .platform-name {
      font-size: 0.7rem;
    }
  }

  .action-buttons {
    flex-direction: column;
    gap: 0.5rem;
  }

  .btn-cancel,
  .btn-download {
    width: 100%;
    justify-content: center;
  }
}
</style>
